import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ChevronLeft,
  MessageSquare,
  Users,
  Save,
  Loader2,
  Plus,
  Trash2,
  AlertCircle,
  CheckCircle,
  ChevronRight,
  Sparkles,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { AppreciationPeriod } from '../../types/supabase';

type SubjectRow = {
  id: string;
  name: string;
  coefficient: number;
};

type StudentRow = {
  id: string;
  first_name: string;
  last_name: string;
  session_examen: number | null;
  numero_candidat: string | null;
};

type ClassInfo = {
  id: string;
  name: string;
  class_number: number;
  period: string;
};

type AppRow = {
  id: string;
  student_id: string;
  subject_id: string | null;
  content: string;
  period: AppreciationPeriod | null;
  start_date: string;
  end_date: string;
  author_id: string;
};

const PERIODS: { value: AppreciationPeriod; label: string; shortLabel: string }[] = [
  { value: 'Y1T1', label: '1ère année - Trimestre 1', shortLabel: 'Y1T1' },
  { value: 'Y1T2', label: '1ère année - Trimestre 2', shortLabel: 'Y1T2' },
  { value: 'Y1T3', label: '1ère année - Trimestre 3', shortLabel: 'Y1T3' },
  { value: 'Y2T1', label: '2ème année - Trimestre 1', shortLabel: 'Y2T1' },
  { value: 'Y2T2', label: '2ème année - Trimestre 2', shortLabel: 'Y2T2' },
  { value: 'Y2T3', label: '2ème année - Trimestre 3', shortLabel: 'Y2T3' },
];

const PERIOD_COLORS: Record<AppreciationPeriod, string> = {
  Y1T1: 'bg-blue-50 text-blue-700 border-blue-200',
  Y1T2: 'bg-indigo-50 text-indigo-700 border-indigo-200',
  Y1T3: 'bg-purple-50 text-purple-700 border-purple-200',
  Y2T1: 'bg-teal-50 text-teal-700 border-teal-200',
  Y2T2: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Y2T3: 'bg-green-50 text-green-700 border-green-200',
};

// Dérive les bornes par défaut d'une période à partir de la session d'examen (calendrier scolaire sept→août)
const periodBounds = (period: AppreciationPeriod, session: number): { start: string; end: string } => {
  const y1Start = session - 2; // septembre de y1Start
  switch (period) {
    case 'Y1T1':
      return { start: `${y1Start}-09-01`, end: `${y1Start}-12-31` };
    case 'Y1T2':
      return { start: `${y1Start + 1}-01-01`, end: `${y1Start + 1}-03-31` };
    case 'Y1T3':
      return { start: `${y1Start + 1}-04-01`, end: `${y1Start + 1}-08-31` };
    case 'Y2T1':
      return { start: `${y1Start + 1}-09-01`, end: `${y1Start + 1}-12-31` };
    case 'Y2T2':
      return { start: `${session}-01-01`, end: `${session}-03-31` };
    case 'Y2T3':
      return { start: `${session}-04-01`, end: `${session}-08-31` };
  }
};

const AdminAppreciations = () => {
  const { classId } = useParams<{ classId: string }>();
  const { user } = useAuth();
  const [classInfo, setClassInfo] = useState<ClassInfo | null>(null);
  const [students, setStudents] = useState<StudentRow[]>([]);
  const [subjects, setSubjects] = useState<SubjectRow[]>([]);
  const [appreciations, setAppreciations] = useState<AppRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [globalError, setGlobalError] = useState<string | null>(null);

  // UI
  const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
  const [periodFilter, setPeriodFilter] = useState<AppreciationPeriod | 'all'>('all');

  // Form
  const [showForm, setShowForm] = useState(false);
  const [formStudentId, setFormStudentId] = useState<string>('');
  const [formSubjectId, setFormSubjectId] = useState<string>('');
  const [formPeriod, setFormPeriod] = useState<AppreciationPeriod>('Y1T1');
  const [formContent, setFormContent] = useState('');
  const [formStartDate, setFormStartDate] = useState('');
  const [formEndDate, setFormEndDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [flash, setFlash] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiTone, setAiTone] = useState<'factuel-bienveillant' | 'exigeant' | 'encourageant'>(
    'factuel-bienveillant'
  );

  useEffect(() => {
    if (!classId) return;
    void load();
  }, [classId]);

  const load = async () => {
    try {
      setIsLoading(true);
      setGlobalError(null);

      // Classe
      const { data: cls, error: clsErr } = await supabase
        .from('classes')
        .select('id, name, class_number, period')
        .eq('id', classId)
        .maybeSingle();
      if (clsErr) throw clsErr;
      if (cls) setClassInfo(cls as ClassInfo);

      // Étudiants
      const { data: studentsData, error: studErr } = await supabase
        .from('students')
        .select('id, first_name, last_name, session_examen, numero_candidat')
        .eq('class_id', classId)
        .order('last_name');
      if (studErr) throw studErr;
      setStudents((studentsData ?? []) as StudentRow[]);

      // Matières de cette classe
      const { data: subjectsData, error: subErr } = await supabase
        .from('subjects')
        .select('id, name, coefficient')
        .eq('class_id', classId)
        .order('name');
      if (subErr) throw subErr;
      setSubjects((subjectsData ?? []) as SubjectRow[]);

      // Appréciations
      if (studentsData && studentsData.length > 0) {
        const ids = studentsData.map((s: any) => s.id);
        const { data: apps, error: aErr } = await supabase
          .from('appreciations')
          .select('id, student_id, subject_id, content, period, start_date, end_date, author_id')
          .in('student_id', ids)
          .order('created_at', { ascending: false });
        if (aErr) throw aErr;
        setAppreciations((apps ?? []) as AppRow[]);
      } else {
        setAppreciations([]);
      }
    } catch (e) {
      console.error('Error loading appreciations:', e);
      setGlobalError('Erreur lors du chargement des appréciations.');
    } finally {
      setIsLoading(false);
    }
  };

  const openFormForStudent = (studentId: string, subjectId = '', period: AppreciationPeriod = 'Y1T1') => {
    const student = students.find((s) => s.id === studentId);
    setFormStudentId(studentId);
    setFormSubjectId(subjectId);
    setFormPeriod(period);
    setFormContent('');
    const session = student?.session_examen ?? new Date().getFullYear();
    const { start, end } = periodBounds(period, session);
    setFormStartDate(start);
    setFormEndDate(end);
    setShowForm(true);
    setFlash(null);
  };

  // Re-sync default dates when period changes
  useEffect(() => {
    if (!showForm || !formStudentId) return;
    const student = students.find((s) => s.id === formStudentId);
    const session = student?.session_examen ?? new Date().getFullYear();
    const { start, end } = periodBounds(formPeriod, session);
    setFormStartDate(start);
    setFormEndDate(end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formPeriod, formStudentId]);

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !classId || !formStudentId) return;
    if (!formContent.trim()) {
      setFlash({ type: 'error', text: "L'appréciation ne peut pas être vide." });
      return;
    }
    if (formContent.length > 1000) {
      setFlash({ type: 'error', text: 'Appréciation trop longue (1000 caractères max).' });
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        student_id: formStudentId,
        class_id: classId,
        subject_id: formSubjectId || null,
        author_id: user.id,
        content: formContent.trim(),
        start_date: formStartDate,
        end_date: formEndDate,
        period: formPeriod,
      };
      const { data, error } = await supabase
        .from('appreciations')
        .insert(payload)
        .select('id, student_id, subject_id, content, period, start_date, end_date, author_id')
        .single();
      if (error) throw error;
      setAppreciations((prev) => [data as AppRow, ...prev]);
      setFlash({ type: 'success', text: 'Appréciation enregistrée.' });
      setFormContent('');
      setTimeout(() => setFlash(null), 3000);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur inconnue';
      setFlash({ type: 'error', text: `Échec : ${msg}` });
    } finally {
      setSubmitting(false);
    }
  };

  const suggestAi = async () => {
    if (!formStudentId) {
      setFlash({ type: 'error', text: "Sélectionne d'abord un étudiant." });
      return;
    }
    if (!formSubjectId) {
      setFlash({
        type: 'error',
        text: "L'IA a besoin d'une matière (les notes sont par matière).",
      });
      return;
    }
    const student = students.find((s) => s.id === formStudentId);
    const session = student?.session_examen ?? new Date().getFullYear();

    setAiLoading(true);
    setFlash(null);
    try {
      const { data, error } = await supabase.functions.invoke('generate-appreciation', {
        body: {
          student_id: formStudentId,
          subject_id: formSubjectId,
          period: formPeriod,
          session_examen: session,
          tone: aiTone,
        },
      });
      if (error) {
        // Edge function retourne le body d'erreur dans error.context.response
        let details = error.message || 'Erreur IA';
        try {
          const ctx = (error as any).context;
          if (ctx?.response) {
            const body = await ctx.response.json();
            details = body.error || details;
          }
        } catch {
          /* ignore */
        }
        throw new Error(details);
      }
      if (!data?.text) throw new Error('Réponse vide');
      setFormContent(data.text);
      setFlash({
        type: 'success',
        text: `Proposition IA générée (${data.nb_grades} note${data.nb_grades > 1 ? 's' : ''}, moy. ${data.moyenne}/20). Relis et ajuste avant d'enregistrer.`,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur IA inconnue';
      setFlash({ type: 'error', text: `IA : ${msg}` });
    } finally {
      setAiLoading(false);
    }
  };

  const deleteAppreciation = async (id: string) => {
    if (!window.confirm('Supprimer cette appréciation ?')) return;
    try {
      const { error } = await supabase.from('appreciations').delete().eq('id', id);
      if (error) throw error;
      setAppreciations((prev) => prev.filter((a) => a.id !== id));
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Erreur');
    }
  };

  const updateAppreciationPeriod = async (id: string, period: AppreciationPeriod | null) => {
    try {
      const { error } = await supabase.from('appreciations').update({ period }).eq('id', id);
      if (error) throw error;
      setAppreciations((prev) =>
        prev.map((a) => (a.id === id ? { ...a, period } : a))
      );
    } catch (e) {
      alert(e instanceof Error ? e.message : 'Erreur');
    }
  };

  const subjectName = (id: string | null): string => {
    if (!id) return 'Appréciation générale';
    return subjects.find((s) => s.id === id)?.name ?? '—';
  };

  const studentName = (id: string): string => {
    const s = students.find((x) => x.id === id);
    return s ? `${s.last_name.toUpperCase()} ${s.first_name}` : '—';
  };

  const filteredAppreciations = useMemo(() => {
    return appreciations.filter((a) => {
      if (selectedStudentId && a.student_id !== selectedStudentId) return false;
      if (periodFilter !== 'all' && a.period !== periodFilter) return false;
      return true;
    });
  }, [appreciations, selectedStudentId, periodFilter]);

  // Stats par étudiant
  const studentStats = useMemo(() => {
    const map = new Map<string, { total: number; byPeriod: Set<AppreciationPeriod> }>();
    for (const s of students) map.set(s.id, { total: 0, byPeriod: new Set() });
    for (const a of appreciations) {
      const st = map.get(a.student_id);
      if (!st) continue;
      st.total += 1;
      if (a.period) st.byPeriod.add(a.period);
    }
    return map;
  }, [appreciations, students]);

  return (
    <div className="animate-fade-in">
      <header className="mb-8">
        <Link to="/admin" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-2">
          <ChevronLeft className="h-4 w-4 mr-1" />
          Retour au tableau de bord
        </Link>
        <div className="flex items-center gap-3 mb-2">
          <MessageSquare className="h-7 w-7 text-primary-600" />
          <h1 className="text-3xl font-bold text-gray-900">Appréciations</h1>
        </div>
        <p className="text-gray-600">
          {classInfo ? (
            <>
              <span className="font-medium">
                {classInfo.name} {classInfo.class_number} ({classInfo.period})
              </span>{' '}
              — saisie des appréciations par période pour le livret scolaire.
            </>
          ) : (
            'Saisie des appréciations par période pour le livret scolaire.'
          )}
        </p>
      </header>

      {globalError && (
        <div className="bg-error-50 border border-error-200 text-error-700 p-4 rounded-md mb-6 flex items-start">
          <AlertCircle className="h-5 w-5 mr-2 mt-0.5" />
          {globalError}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Sidebar étudiants */}
        <aside className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b border-gray-200">
              <h2 className="font-semibold text-gray-900 inline-flex items-center">
                <Users className="h-4 w-4 mr-2" /> Étudiants ({students.length})
              </h2>
            </div>
            <ul className="divide-y divide-gray-200 max-h-[70vh] overflow-y-auto">
              <li>
                <button
                  onClick={() => setSelectedStudentId(null)}
                  className={`w-full text-left p-3 hover:bg-gray-50 flex items-center justify-between ${
                    selectedStudentId === null ? 'bg-primary-50 border-l-4 border-primary-500' : ''
                  }`}
                >
                  <span className="text-sm font-medium text-gray-900">
                    Tous les étudiants
                  </span>
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </button>
              </li>
              {students.map((s) => {
                const st = studentStats.get(s.id);
                return (
                  <li key={s.id}>
                    <button
                      onClick={() => setSelectedStudentId(s.id)}
                      className={`w-full text-left p-3 hover:bg-gray-50 ${
                        selectedStudentId === s.id
                          ? 'bg-primary-50 border-l-4 border-primary-500'
                          : ''
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="text-sm font-medium text-gray-900">
                            {s.last_name.toUpperCase()} {s.first_name}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5">
                            {st?.total ?? 0} appréciation{(st?.total ?? 0) !== 1 ? 's' : ''} ·{' '}
                            {st?.byPeriod.size ?? 0}/6 périodes
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openFormForStudent(s.id);
                          }}
                          className="text-xs bg-primary-600 text-white px-2 py-1 rounded hover:bg-primary-700 inline-flex items-center"
                          title="Ajouter une appréciation"
                        >
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </aside>

        {/* Contenu principal */}
        <section className="lg:col-span-3">
          {/* Filtres */}
          <div className="bg-white rounded-lg shadow p-4 mb-4 flex flex-wrap gap-2 items-center">
            <span className="text-sm font-medium text-gray-700 mr-2">Période :</span>
            <button
              onClick={() => setPeriodFilter('all')}
              className={`text-xs px-3 py-1 rounded-full border transition ${
                periodFilter === 'all'
                  ? 'bg-gray-900 text-white border-gray-900'
                  : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
              }`}
            >
              Toutes
            </button>
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriodFilter(p.value)}
                className={`text-xs px-3 py-1 rounded-full border transition ${
                  periodFilter === p.value
                    ? `${PERIOD_COLORS[p.value]} border-current font-medium`
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
              >
                {p.shortLabel}
              </button>
            ))}
            <div className="ml-auto">
              <button
                onClick={() => {
                  if (selectedStudentId) openFormForStudent(selectedStudentId);
                  else if (students.length > 0) openFormForStudent(students[0].id);
                }}
                className="btn btn-primary inline-flex items-center gap-2 text-sm"
              >
                <Plus className="h-4 w-4" />
                Nouvelle appréciation
              </button>
            </div>
          </div>

          {/* Form modal */}
          {showForm && (
            <div className="bg-white rounded-lg shadow p-5 mb-4 border-l-4 border-primary-500">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-gray-900">Nouvelle appréciation</h3>
                <button
                  onClick={() => setShowForm(false)}
                  className="text-gray-400 hover:text-gray-700 text-sm"
                >
                  Fermer
                </button>
              </div>
              {flash && (
                <div
                  className={`mb-3 p-2 rounded text-sm flex items-start ${
                    flash.type === 'success'
                      ? 'bg-success-50 text-success-700'
                      : 'bg-error-50 text-error-700'
                  }`}
                >
                  {flash.type === 'success' ? (
                    <CheckCircle className="h-4 w-4 mr-1 mt-0.5" />
                  ) : (
                    <AlertCircle className="h-4 w-4 mr-1 mt-0.5" />
                  )}
                  {flash.text}
                </div>
              )}
              <form onSubmit={submitForm} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Étudiant</label>
                    <select
                      value={formStudentId}
                      onChange={(e) => setFormStudentId(e.target.value)}
                      className="input-field py-1.5 w-full"
                      required
                    >
                      <option value="">— Choisir —</option>
                      {students.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.last_name.toUpperCase()} {s.first_name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Matière</label>
                    <select
                      value={formSubjectId}
                      onChange={(e) => setFormSubjectId(e.target.value)}
                      className="input-field py-1.5 w-full"
                    >
                      <option value="">Appréciation générale</option>
                      {subjects.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Période</label>
                    <select
                      value={formPeriod}
                      onChange={(e) => setFormPeriod(e.target.value as AppreciationPeriod)}
                      className="input-field py-1.5 w-full"
                      required
                    >
                      {PERIODS.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Du</label>
                    <input
                      type="date"
                      value={formStartDate}
                      onChange={(e) => setFormStartDate(e.target.value)}
                      className="input-field py-1.5 w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">Au</label>
                    <input
                      type="date"
                      value={formEndDate}
                      onChange={(e) => setFormEndDate(e.target.value)}
                      className="input-field py-1.5 w-full"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-xs font-medium text-gray-700">
                      Appréciation
                    </label>
                    <div className="flex items-center gap-2">
                      <select
                        value={aiTone}
                        onChange={(e) =>
                          setAiTone(
                            e.target.value as
                              | 'factuel-bienveillant'
                              | 'exigeant'
                              | 'encourageant'
                          )
                        }
                        className="text-xs border border-gray-300 rounded px-2 py-1 bg-white text-gray-700"
                        title="Ton souhaité pour la proposition IA"
                      >
                        <option value="factuel-bienveillant">Ton : factuel-bienveillant</option>
                        <option value="exigeant">Ton : exigeant</option>
                        <option value="encourageant">Ton : encourageant</option>
                      </select>
                      <button
                        type="button"
                        onClick={suggestAi}
                        disabled={aiLoading || !formStudentId || !formSubjectId}
                        className="text-xs inline-flex items-center gap-1 px-2.5 py-1 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 disabled:cursor-not-allowed"
                        title={
                          !formSubjectId
                            ? 'Sélectionne une matière pour activer l\u2019IA'
                            : 'Propose une appréciation générée par IA à partir des notes'
                        }
                      >
                        {aiLoading ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <Sparkles className="h-3 w-3" />
                        )}
                        Suggérer IA
                      </button>
                    </div>
                  </div>
                  <textarea
                    value={formContent}
                    onChange={(e) => setFormContent(e.target.value)}
                    rows={4}
                    maxLength={1000}
                    className="input-field w-full"
                    placeholder="Rédigez l'appréciation pour cette période... ou cliquez sur « Suggérer IA » pour obtenir une proposition à partir des notes."
                    required
                  />
                  <p className="text-xs text-gray-400 mt-0.5">
                    {formContent.length}/1000 · L'IA s'appuie sur les notes réelles de la période et
                    génère une proposition éditable avant enregistrement.
                  </p>
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowForm(false)}
                    className="btn btn-outline"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="btn btn-primary inline-flex items-center gap-2 disabled:opacity-50"
                  >
                    {submitting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save className="h-4 w-4" />
                    )}
                    Enregistrer
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Liste des appréciations */}
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary-600" />
            </div>
          ) : filteredAppreciations.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              {selectedStudentId || periodFilter !== 'all'
                ? 'Aucune appréciation pour ce filtre.'
                : 'Aucune appréciation enregistrée pour cette classe.'}
            </div>
          ) : (
            <div className="space-y-3">
              {filteredAppreciations.map((a) => (
                <div key={a.id} className="bg-white rounded-lg shadow p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      {!selectedStudentId && (
                        <h4 className="font-semibold text-gray-900">
                          {studentName(a.student_id)}
                        </h4>
                      )}
                      <div className="flex items-center gap-2 flex-wrap mt-1">
                        <span className="text-xs text-gray-500">
                          {subjectName(a.subject_id)}
                        </span>
                        <select
                          value={a.period ?? ''}
                          onChange={(e) =>
                            updateAppreciationPeriod(
                              a.id,
                              (e.target.value || null) as AppreciationPeriod | null
                            )
                          }
                          className={`text-xs px-2 py-0.5 rounded-full border ${
                            a.period
                              ? PERIOD_COLORS[a.period]
                              : 'bg-gray-100 text-gray-500 border-gray-300'
                          }`}
                        >
                          <option value="">— sans période —</option>
                          {PERIODS.map((p) => (
                            <option key={p.value} value={p.value}>
                              {p.shortLabel}
                            </option>
                          ))}
                        </select>
                        <span className="text-xs text-gray-400">
                          {new Date(a.start_date).toLocaleDateString('fr-FR')} →{' '}
                          {new Date(a.end_date).toLocaleDateString('fr-FR')}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={() => deleteAppreciation(a.id)}
                      className="text-gray-400 hover:text-error-600 transition p-1"
                      title="Supprimer"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="text-sm text-gray-700 whitespace-pre-line">{a.content}</p>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminAppreciations;
